import { Injectable } from '@angular/core';
import {
  Room,
  RoomEvent,
  Track,
  LocalParticipant,
  LocalTrackPublication,
  RemoteParticipant,
  RemoteTrackPublication,
  RoomConnectOptions,
  TrackPublishOptions,
  AudioPresets,
  VideoPresets,
  CreateLocalTracksOptions,
  LocalAudioTrack,
  ConnectionState,RemoteAudioTrack
} from 'livekit-client';
import { Subject, BehaviorSubject } from 'rxjs';

export interface LiveKitConfig {
  url: string; // LiveKit server URL (e.g., ws://localhost:7880 or wss://your-server.com)
  token: string; // JWT token from your LiveKit server
  roomName?: string; // Optional room name
  participantIdentity?: string;  // ParticipantIdentity
  participantName?: string;      // ParticipantName
  role?: string;        //  Role
}

export interface LiveKitStatus {
  connected: boolean;
  roomName: string;
  participantCount: number;
  error?: string;
}

export interface LiveKitParticipant {
  identity: string;
  name: string;
  isLocal: boolean;
}

export interface LiveKitDataMessage {
  payload: Uint8Array;
  participant?: RemoteParticipant;
  kind?: any;
  topic?: string;
}

export interface LiveKitNativeTranscriptionEvent {
  transcriptions: any[];
  participant?: RemoteParticipant;
  publication?: any;
}

@Injectable({
  providedIn: 'root'
})
export class LiveKitStreamingService {
  private room: Room | null = null;
  private localAudioTrack: LocalAudioTrack | null = null;
  private audioContext: AudioContext | null = null;
  private audioWorkletNode: AudioWorkletNode | null = null;
  private mediaStreamDestination: MediaStreamAudioDestinationNode | null = null;

  // Observable subjects for status updates
  private connectionStatusSubject = new BehaviorSubject<LiveKitStatus>({
    connected: false,
    roomName: '',
    participantCount: 0
  });
  public connectionStatus$ = this.connectionStatusSubject.asObservable();

  private errorSubject = new Subject<string>();
  public error$ = this.errorSubject.asObservable();

  private transcriptionSubject = new Subject<any>();
  public transcription$ = this.transcriptionSubject.asObservable();

  private dataReceivedSubject = new Subject<LiveKitDataMessage>();
  public dataReceived$ = this.dataReceivedSubject.asObservable();

  private nativeTranscriptionSubject = new Subject<LiveKitNativeTranscriptionEvent>();
  public nativeTranscription$ = this.nativeTranscriptionSubject.asObservable();

  private participantsSubject = new BehaviorSubject<LiveKitParticipant[]>([]);
  public participants$ = this.participantsSubject.asObservable();

  constructor() {
    // console.log('LiveKitStreamingService initialized');
  }

  /**
   * Connect to a LiveKit room
   * @param config LiveKit configuration with URL and token
   */
  async connect(config: LiveKitConfig): Promise<void> {
    try {
      // console.log('🔵 Connecting to LiveKit server...');

      if (this.room) {
        // console.log('⚠️ Already connected to a room, disconnecting first...');
        await this.disconnect();
      }

      // Create new room instance
      this.room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution
        },
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true
        }
      });

      // Setup room event listeners
      this.setupRoomEvents();

      // Connect to the room
      const roomName = config.roomName || 'audio-streaming-room';
      await this.room.connect(config.url, config.token);
      this.updateParticipants();

      // console.log('✅ Connected to LiveKit room:', roomName);
      this.updateStatus({
        connected: true,
        roomName: roomName,
        participantCount: this.room.numParticipants
      });

    } catch (error: any) {
      console.error('❌ Failed to connect to LiveKit:', error);
      const errorMessage = `LiveKit connection failed: ${error.message || error}`;
      this.errorSubject.next(errorMessage);
      this.updateStatus({ connected: false, roomName: '', participantCount: 0, error: errorMessage });
      throw error;
    }
  }

  /**
   * Setup room event listeners for LiveKit
   */
  private setupRoomEvents(): void {
    if (!this.room) return;

    this.room.on(RoomEvent.Connected, () => {
      // console.log('🟢 LiveKit room connected');
      this.updateParticipants();
      this.updateStatus({
        connected: true,
        roomName: this.room?.name || '',
        participantCount: this.room?.numParticipants || 0
      });
    });

    this.room.on(RoomEvent.Disconnected, (reason?: any) => {
      // console.log('🔴 LiveKit room disconnected:', reason);
      this.participantsSubject.next([]);
      this.updateStatus({
        connected: false,
        roomName: '',
        participantCount: 0
      });
    });

    this.room.on(RoomEvent.Reconnecting, () => {
      // console.log('🔄 LiveKit reconnecting...');
    });

    this.room.on(RoomEvent.Reconnected, () => {
      // console.log('🟢 LiveKit reconnected');
      this.updateParticipants();
      this.updateStatus({
        connected: true,
        roomName: this.room?.name || '',
        participantCount: this.room?.numParticipants || 0
      });
    });

    this.room.on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
      // console.log('👤 Participant connected:', participant.identity);
      this.updateParticipants();
      this.updateStatus({
        connected: true,
        roomName: this.room?.name || '',
        participantCount: this.room?.numParticipants || 0
      });
    });

    this.room.on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
      console.log('👤 Participant disconnected:', participant.identity);
      this.updateParticipants();
      this.updateStatus({
        connected: true,
        roomName: this.room?.name || '',
        participantCount: this.room?.numParticipants || 0
      });
    });

    // Listen for data messages (e.g., transcriptions from server)
    this.room.on(RoomEvent.DataReceived, (...args: any[]) => {
      const payload: Uint8Array = args[0];
      const participant: RemoteParticipant | undefined = args[1];
      const kind = args[2];
      const topic = args[3];

      this.dataReceivedSubject.next({ payload, participant, kind, topic });

      try {
        const decoder = new TextDecoder();
        const message = decoder.decode(payload);
        const data = JSON.parse(message);
        
        // console.log('📨 Data received from LiveKit:', data);
        
        // Handle transcription payloads from different server shapes.
        const eventType = String(data?.type || data?.event || '').toLowerCase();
        const hasTranscriptText = !!(
          data?.text ||
          data?.transcript ||
          data?.message ||
          data?.payload?.text ||
          data?.data?.text
        );

        if (eventType.includes('transcript') || eventType === 'transcription' || hasTranscriptText) {
          this.transcriptionSubject.next(data);
        }
      } catch (error) {
        console.error('❌ Error parsing data from LiveKit:', error);
      }
    });

   
    // Handle native LiveKit transcription events when available in the SDK/runtime.
    const roomAny = this.room as any;
    const transcriptionEventName = (RoomEvent as any).TranscriptionReceived || 'transcriptionReceived';
    if (roomAny && typeof roomAny.on === 'function') {
      roomAny.on(transcriptionEventName, (...args: any[]) => {
        const transcriptions = Array.isArray(args[0]) ? args[0] : [];
        const participant: RemoteParticipant | undefined = args[1];
        const publication = args[2];

        this.nativeTranscriptionSubject.next({
          transcriptions,
          participant,
          publication
        });
      });
    }

    this.room.on(RoomEvent.ConnectionQualityChanged, (quality: any, participant: any) => {
      // console.log('📊 Connection quality changed:', quality, participant?.identity || 'local');
    });

    this.room.on(RoomEvent.TrackPublished, (publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      // console.log('✅ Track published:', publication.kind);
    });

    this.room.on(RoomEvent.TrackUnpublished, (publication: RemoteTrackPublication, participant: RemoteParticipant) => {
      // console.log('❌ Track unpublished:', publication.kind);
    });
    this.room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {

      if (track.kind === 'audio') {
        const audioTrack = track as RemoteAudioTrack;
    
        const audioElement = audioTrack.attach();
        document.body.appendChild(audioElement);
      }
    
    });

    this.room.on(RoomEvent.TrackUnsubscribed, (track) => {
      if (track.kind === 'audio') {
        track.detach().forEach(element => element.remove());
      }
    });
  }

  /**
   * Start streaming audio from a MediaStream to LiveKit
   * @param stream MediaStream from microphone or other audio source
   */
  async startAudioStreaming(stream: MediaStream): Promise<void> {
    try {
      if (!this.room || this.room.state !== ConnectionState.Connected) {
        throw new Error('Not connected to LiveKit room');
      }

      // console.log('🎤 Starting audio streaming to LiveKit...');

      // Get audio track from stream
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No audio tracks found in stream');
      }

      // Create LocalAudioTrack from the stream
      this.localAudioTrack = new LocalAudioTrack(audioTracks[0]);

      // Publish audio track to room
      await this.room.localParticipant.publishTrack(this.localAudioTrack, {
        name: 'microphone',
        source: Track.Source.Microphone,
        dtx: true // Discontinuous Transmission to save bandwidth on silence
      });

      // console.log('✅ Audio track published to LiveKit');

    } catch (error: any) {
      console.error('❌ Failed to start audio streaming:', error);
      const errorMessage = `Failed to stream audio: ${error.message || error}`;
      this.errorSubject.next(errorMessage);
      throw error;
    }
  }

  /**
   * Stream audio from ArrayBuffer (for IndexedDB audio chunks)
   * This creates a virtual audio track from raw audio data
   * @param audioBuffer ArrayBuffer containing audio data
   * @param sampleRate Sample rate of the audio (default: 16000)
   * @param channels Number of channels (default: 1 for mono)
   */
  async streamAudioFromBuffer(
    audioBuffer: ArrayBuffer,
    sampleRate: number = 16000,
    channels: number = 1
  ): Promise<void> {
    try {
      if (!this.room || this.room.state !== ConnectionState.Connected) {
        throw new Error('Not connected to LiveKit room');
      }

      // Initialize AudioContext if not already done
      if (!this.audioContext) {
        this.audioContext = new AudioContext({ sampleRate });
        console.log('🎵 AudioContext created for LiveKit streaming');
      }

      // Create audio buffer from raw data
      const audioCtx = this.audioContext;
      const buffer = await audioCtx.decodeAudioData(audioBuffer.slice(0));

      // Create a buffer source
      const source = audioCtx.createBufferSource();
      source.buffer = buffer;

      // Create MediaStreamDestination if not exists
      if (!this.mediaStreamDestination) {
        this.mediaStreamDestination = audioCtx.createMediaStreamDestination();
      }

      // Connect source to destination
      source.connect(this.mediaStreamDestination);

      // If this is the first chunk, create and publish the track
      if (!this.localAudioTrack) {
        const mediaStream = this.mediaStreamDestination.stream;
        await this.startAudioStreaming(mediaStream);
      }

      // Play the audio source
      source.start(0);

      console.log(`📤 Streamed ${audioBuffer.byteLength} bytes to LiveKit`);

    } catch (error: any) {
      console.error('❌ Failed to stream audio buffer:', error);
      const errorMessage = `Failed to stream audio buffer: ${error.message || error}`;
      this.errorSubject.next(errorMessage);
      throw error;
    }
  }

  /**
   * Stream audio chunk as base64 (for compatibility with existing implementation)
   * @param base64Audio Base64 encoded audio data
   * @param metadata Optional metadata about the audio
   */
  async streamAudioChunk(base64Audio: string, metadata?: any): Promise<void> {
    try {
      if (!this.room || this.room.state !== ConnectionState.Connected) {
        throw new Error('Not connected to LiveKit room');
      }

      // Decode base64 to ArrayBuffer
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Send as data message to the room (to be processed by server)
      const payload = {
        type: 'audio_chunk',
        audio: base64Audio,
        metadata: metadata || {},
        timestamp: Date.now()
      };

      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(payload));
      
      await this.room.localParticipant.publishData(data, { reliable: true });

      console.log(`📤 Sent audio chunk to LiveKit (${bytes.length} bytes)`);

    } catch (error: any) {
      console.error('❌ Failed to send audio chunk:', error);
      const errorMessage = `Failed to send audio chunk: ${error.message || error}`;
      this.errorSubject.next(errorMessage);
      throw error;
    }
  }

  /**
   * Mute the published audio track (pause - stops sending audio to server)
   */
  async muteAudio(): Promise<void> {
    if (this.localAudioTrack) {
      await this.localAudioTrack.mute();
      console.log('⏸️ LiveKit audio track muted');
    }
  }

  /**
   * Unmute the published audio track (resume - resumes sending audio to server)
   */
  async unmuteAudio(): Promise<void> {
    if (this.localAudioTrack) {
      await this.localAudioTrack.unmute();
      console.log('▶️ LiveKit audio track unmuted');
    }
  }

  /**
   * Replace the currently published local audio track with a new input track.
   */
  async replaceAudioInputTrack(newTrack: MediaStreamTrack): Promise<void> {
    if (!this.room || this.room.state !== ConnectionState.Connected) {
      return;
    }

    const currentTrack: any = this.localAudioTrack as any;

    // Prefer in-place track replacement so publication/subscriptions stay stable for
    // downstream transcription components.
    if (currentTrack && typeof currentTrack.setMediaStreamTrack === 'function') {
      const wasMuted = !!currentTrack.isMuted;
      await currentTrack.setMediaStreamTrack(newTrack, true);
      if (wasMuted) {
        await currentTrack.mute();
      }
      console.log('🔁 LiveKit local audio track source replaced in-place');
      return;
    }

    // Fallback path for runtimes without setMediaStreamTrack support.
    const replacementTrack = new LocalAudioTrack(newTrack);
    if (this.localAudioTrack) {
      await this.room.localParticipant.unpublishTrack(this.localAudioTrack);
      this.localAudioTrack.stop();
    }
    this.localAudioTrack = replacementTrack;
    await this.room.localParticipant.publishTrack(this.localAudioTrack, {
      name: 'microphone',
      source: Track.Source.Microphone,
      dtx: true
    });
    // console.log('🔁 LiveKit local audio track replaced by republish fallback');
  }

  /**
   * Stop audio streaming
   */
  async stopAudioStreaming(): Promise<void> {
    try {
      // console.log('🛑 Stopping audio streaming...');

      if (this.localAudioTrack) {
        if (this.room && this.room.state === ConnectionState.Connected) {
          await this.room.localParticipant.unpublishTrack(this.localAudioTrack);
        }
        this.localAudioTrack.stop();
        this.localAudioTrack = null;
      }

      if (this.audioWorkletNode) {
        this.audioWorkletNode.disconnect();
        this.audioWorkletNode = null;
      }

      if (this.mediaStreamDestination) {
        this.mediaStreamDestination.disconnect();
        this.mediaStreamDestination = null;
      }

      // console.log('✅ Audio streaming stopped');

    } catch (error: any) {
      console.error('❌ Error stopping audio streaming:', error);
      this.errorSubject.next(`Failed to stop streaming: ${error.message || error}`);
    }
  }

  /**
   * Disconnect from LiveKit room
   */
  async disconnect(): Promise<void> {
    try {
      // console.log('🔴 Disconnecting from LiveKit...');

      await this.stopAudioStreaming();

      if (this.room) {
        await this.room.disconnect();
        this.room = null;
      }

      if (this.audioContext) {
        await this.audioContext.close();
        this.audioContext = null;
      }

      this.updateStatus({
        connected: false,
        roomName: '',
        participantCount: 0
      });

      // console.log('✅ Disconnected from LiveKit');

    } catch (error: any) {
      console.error('❌ Error disconnecting from LiveKit:', error);
      this.errorSubject.next(`Disconnect error: ${error.message || error}`);
    }
  }

  /**
   * Check if connected to LiveKit
   */
  isConnected(): boolean {
    return this.room !== null && this.room.state === ConnectionState.Connected;
  }

  /**
   * Get latest participant snapshot
   */
  getParticipants(): LiveKitParticipant[] {
    return this.participantsSubject.value;
  }

  /**
   * Get current room name
   */
  getRoomName(): string {
    return this.room?.name || '';
  }

  /**
   * Get participant count
   */
  getParticipantCount(): number {
    return this.room ? this.room.numParticipants : 0;
  }

  /**
   * Update connection status
   */
  private updateStatus(status: LiveKitStatus): void {
    this.connectionStatusSubject.next(status);
  }

  private updateParticipants(): void {
    if (!this.room || this.room.state !== ConnectionState.Connected) {
      this.participantsSubject.next([]);
      return;
    }

    const participants: LiveKitParticipant[] = [];

    const localIdentity = this.room.localParticipant?.identity || 'local-host';
    //console.log("localIdentity",localIdentity);
    participants.push({
      identity: localIdentity,
      //name: this.displayParticipantName(localIdentity),
      name: this.room.localParticipant?.name || localIdentity,
      isLocal: true
    });

    this.room.remoteParticipants.forEach((remoteParticipant: RemoteParticipant) => {
      const identity = remoteParticipant.identity || 'participant';
      participants.push({
        identity,
        name: this.displayParticipantName(identity),
        isLocal: false
      });
    });

    this.participantsSubject.next(participants);
  }

  private displayParticipantName(identity: string): string {
    if (!identity) {
      return 'Participant';
    }

    return identity.replace(/[_-]+/g, ' ').trim();
  }

  /**
   * Send custom data to the room
   * @param data Data to send
   * @param reliable Whether to send reliably (default: true)
   */
  async sendData(data: any, reliable: boolean = true): Promise<void> {
    try {
      if (!this.room || this.room.state !== ConnectionState.Connected) {
        throw new Error('Not connected to LiveKit room');
      }

      const encoder = new TextEncoder();
      const payload = encoder.encode(JSON.stringify(data));
      
      await this.room.localParticipant.publishData(payload, { reliable });
      
      console.log('📤 Data sent to LiveKit:', data);

    } catch (error: any) {
      console.error('❌ Failed to send data:', error);
      this.errorSubject.next(`Failed to send data: ${error.message || error}`);
      throw error;
    }
  }
}
